import { useCallback, useMemo } from "react";
import { useRouter } from "expo-router";

import { useAuthStore } from "@src/features/auth/auth-store";
import { authenticatedFetch } from "@src/features/auth/services/authenticated-fetch";
import { getEnvironment } from "@src/config/environment-store";
import { directory } from "@src/config/runtime";
import { showInfoToast } from "@src/utils/toast";
import {
  createDeviceTokenApi,
  usePushNotifications as usePushNotificationsBase,
  teardownDeviceToken,
} from "@visioni/a3s-notifications/app";
import type { DeviceTokenApi } from "@visioni/a3s-notifications/app";

async function directoryBaseUrl(): Promise<string> {
  const env = await getEnvironment();
  return directory(env);
}

let sharedApiClient: DeviceTokenApi | undefined;

function getDeviceTokenApi(): DeviceTokenApi {
  sharedApiClient ??= createDeviceTokenApi({
    authenticatedFetch,
    directoryBaseUrl,
  });
  return sharedApiClient;
}

export function usePushNotifications(options?: { enabled?: boolean }) {
  const router = useRouter();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  const deviceTokenApi = useMemo(() => getDeviceTokenApi(), []);
  const showForegroundToast = useCallback(
    (title: string, body?: string) => showInfoToast({ message: title, description: body }),
    [],
  );

  usePushNotificationsBase({
    auth: { isAuthenticated, isInitialized },
    router,
    showForegroundToast,
    deviceTokenApi,
    enabled: options?.enabled ?? true,
  });
}

export async function unregisterPushToken(): Promise<void> {
  return teardownDeviceToken(getDeviceTokenApi(), { lookupToken: true });
}

export { getDeviceTokenApi };
